import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"

interface Options {
  /** Cmd/Ctrl+K — open global search. */
  onSearch: () => void
  /** Cmd/Ctrl+/ — open the keyboard-shortcuts modal. */
  onShortcuts?: () => void
}

/**
 * Global keyboard shortcuts for the Client Admin portal.
 *
 *   ⌘K / Ctrl+K   open search
 *   ⌘/ / Ctrl+/   show shortcuts
 *   g d           go to Dashboard      (g-prefixed sequence)
 *   g a           go to Assessments
 *   g c           go to Candidates
 *   g r           go to Reports
 *
 * Sequence keys are ignored while typing in an input/textarea/select or a
 * contenteditable element so they never hijack normal typing.
 */
export function useKeyboardShortcuts({ onSearch, onShortcuts }: Options) {
  const navigate = useNavigate()
  // Timestamp of the last "g" press — a following key within 1s triggers
  // the matching go-to navigation.
  const gPressedAt = useRef(0)

  useEffect(() => {
    function isTypingTarget(el: EventTarget | null): boolean {
      const node = el as HTMLElement | null
      if (!node) return false
      const tag = node.tagName
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        node.isContentEditable === true
      )
    }

    function handler(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey

      // ⌘K / Ctrl+K — always available, even from inputs.
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault()
        onSearch()
        return
      }
      // ⌘/ / Ctrl+/ — shortcuts help.
      if (mod && e.key === "/") {
        e.preventDefault()
        onShortcuts?.()
        return
      }

      if (isTypingTarget(e.target) || mod || e.altKey) return

      // g-prefixed navigation sequences.
      if (e.key === "g") {
        gPressedAt.current = Date.now()
        return
      }
      if (Date.now() - gPressedAt.current < 1000) {
        const dest: Record<string, string> = {
          d: "/client/dashboard",
          a: "/client/projects",
          c: "/client/candidates",
          r: "/client/reports",
        }
        const to = dest[e.key.toLowerCase()]
        if (to) {
          e.preventDefault()
          gPressedAt.current = 0
          navigate(to)
        }
      }
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [navigate, onSearch, onShortcuts])
}
